// Note, NProgress isn't a React specific library
// but it attaches to DOM outside of React mount point
// so we can use it w/o issues.
import NProgress from 'nprogress';
import { printAST } from 'apollo-client';
import { ApolloLink, Observable } from 'apollo-link';
import extractFiles from 'extract-files';
import objectPath from 'object-path';

import { jwtStore, isObject } from 'shared/helpers';



/*
 * Middlware factory, for file uploads.
 * Extracts files and send multipart/form-data content type request.
 *
 * References:
 * https://hexdocs.pm/absinthe/file-uploads.html
 * https://github.com/bytewitchcraft/apollo-absinthe-upload-link
 * https://github.com/jaydenseric/apollo-upload-client/
 * https://medium.com/@danielbuechele/file-uploads-with-graphql-and-apollo-5502bbf3941e
 *
 */

export const FileUploadLinkMid = ({ uri }) => new ApolloLink((operation, forward) => {
  const { variables, query } = operation;

  if (typeof FormData === 'undefined' || !isObject(variables)) {
    return forward(operation);
  }

  const files = extractFiles(variables);
  if (!files.length) {
    return forward(operation);
  }

  // extractFiles removes extracted file nodes from tree
  // (i.e. variables) above, so re-populate with file name instead.
  const treePath = objectPath(variables);
  files.forEach(
    ({path, file}) => treePath.set(path, path)
  );

  return new Observable(observer => {
    const formData = new FormData();

    formData.append('query', printAST(query));
    formData.append('variables', JSON.stringify(variables));
    files.forEach(
      ({path, file}) => formData.append(path, file)
    );

    const fetchOpts = {
      body: formData,
      method: 'POST',
    };

    fetch(uri, fetchOpts)
      .then(resp => {
        if (!resp.ok) {
          throw new Error(`${resp.status} (${resp.statusText})`)
        }
        return resp.json();
      })
      .then(result => {
        observer.next(result);
        observer.complete();
        return forward(operation);  // for afterware?
      })
      .catch(error => {
        observer.error(error);
        return forward(operation);// for afterware?
      });
  });

});



/*
 * Middlware,
 * to always send jwt in header if available
 */
export const setAuthHeaderMid = new ApolloLink((operation, forward) => {
  // TODO(tyu): Maybe just import Redux store to look up jwt.
  const jwt = jwtStore.get();

  operation.setContext({
    headers: {
      authorization: jwt ? `Bearer ${jwt}` : null,
    }
  });

  return forward(operation);
});



/*
 * Middlware,
 * to show progress bar for each request, unless excluded.
 */
NProgress.configure({ showSpinner: false });
// Don't show progress bar for below gql operations:
const stealthProgressOps = [
  'signupInputLivecheck',
];

export const showProgressBarMid = new ApolloLink((operation, forward) => {
  // Exclude some Apollo requests from automatically showing progress bar.
  // Preferably we want to specify such a thing from query origin but
  // arbitrary kv data like `metadata` gets stripped off by Apollo before
  // making it to middleware. :\ For now, just exclude by operation name.
  // Reference: https://github.com/apollographql/apollo-client/issues/1066
  if (stealthProgressOps.includes(operation.operationName)) {
    return forward(operation);
  }

  NProgress.start();
  return forward(operation);
});




/*
 * Afterware,
 * to hide progress bar when request concludes.
 */
export const hideProgressBarAfter = new ApolloLink((operation, forward) => {
  NProgress.done();

  return forward(operation);
});



// WEBPACK FOOTER //
// ./src/helpers/apolloLinks.js